import {sectionNames} from '@github-ui/mergebox/components/sections/checks/StatusCheckStatesIcon'
import {checksSectionHeading} from '@github-ui/mergebox/components/sections/checks/checks-section-header-text'
import {
  isFailingOrIncompleteStatus,
  isPendingStatus,
  type StatusCheck,
  type StatusCheckState,
  type StatusRollup,
} from '@github-ui/mergebox/page-data/payloads/status-checks'

import type {MiniStatusIconProps} from './mini-merge-sections/MiniStatusIcon'
import type {ProgressSegment} from './mini-merge-sections/MultiColorProgressBar'

export type ChecksProgressProjection = {
  successful: number
  total: number
  isUnavailable: boolean
  icon: MiniStatusIconProps['type']
  status?: string
  progressLabel: string
  progressValueText?: string
  blockingSummary?: string
  segments: readonly ProgressSegment[]
}

type ChecksSectionStatusType = keyof typeof checksSectionHeading

const iconByCombinedState: Record<ChecksSectionStatusType, MiniStatusIconProps['type']> = {
  PASSED: 'success',
  PENDING: 'warning',
  PENDING_APPROVAL: 'warning',
  PENDING_CONFLICTS: 'warning',
  FAILED: 'failure',
  PENDING_FAILED: 'failure',
  SOME_FAILED: 'failure',
  UNKNOWN: 'warning',
}

const failureStates = new Set(sectionNames.FAILURE)
const attentionRequiredStates = ['ACTION_REQUIRED', '_UNKNOWN_VALUE'] as const

export function projectChecksProgress(
  statusRollup: StatusRollup | null | undefined,
  statusChecks: readonly StatusCheck[] = [],
  sectionStatus: ChecksSectionStatusType | undefined = statusRollup?.combinedState,
): ChecksProgressProjection {
  const summary = statusRollup?.summary ?? []
  const passed = countStates(summary, ['SUCCESS'])
  const skipped = countStates(summary, ['SKIPPED'])
  const neutral = countStates(summary, ['NEUTRAL'])
  const completed = countStates(summary, ['COMPLETED'])
  const pending = countMatchingStates(summary, state => state === 'REQUESTED' || isPendingStatus(state) === true)
  const attentionRequired = countStates(summary, attentionRequiredStates)
  const failing = countStates(summary, sectionNames.FAILURE)
  const optionalFailing = Math.min(
    failing,
    statusChecks.filter(check => !check.isRequired && failureStates.has(check.state)).length,
  )
  const requiredFailing = Math.min(
    failing - optionalFailing,
    statusChecks.filter(check => check.isRequired && failureStates.has(check.state)).length,
  )
  const unmatchedFailing = failing - optionalFailing - requiredFailing
  const requiredPending = statusChecks.filter(
    check => check.isRequired && (check.state === 'REQUESTED' || isPendingStatus(check.state)),
  ).length
  const requiredAttention = Math.min(
    attentionRequired,
    statusChecks.filter(check => check.isRequired && isAttentionRequiredStatus(check.state)).length,
  )
  const hasRequiredBlocker = statusChecks.some(check => check.isRequired && isFailingOrIncompleteStatus(check.state))
  const successful = passed + skipped + neutral
  const pendingWorkflowCount = statusRollup?.pendingWorkflowApprovalRollup?.workflowsRequiringApprovalCount ?? 0
  const total = summary.reduce((sum, state) => sum + state.count, 0)
  const unclassifiedCount = Math.max(
    0,
    total - passed - skipped - neutral - completed - pending - attentionRequired - failing,
  )
  const segments = total
    ? [
        segment('success', passed, total, 'successful check', 'checks-progress-success'),
        segment('neutral', skipped, total, 'skipped check', 'checks-progress-skipped'),
        segment('neutral', neutral, total, 'neutral check', 'checks-progress-neutral'),
        segment(
          'neutral',
          completed,
          total,
          'completed check status',
          'checks-progress-completed',
          'completed check statuses',
        ),
        segment('attention', pending, total, 'pending check', 'checks-progress-pending'),
        segment(
          'attention',
          attentionRequired,
          total,
          'check needing attention',
          'checks-progress-attention-required',
          'checks needing attention',
        ),
        segment(
          'neutral',
          unclassifiedCount,
          total,
          'pending check status',
          'checks-progress-active-state',
          'pending check statuses',
        ),
        segment('danger', unmatchedFailing, total, 'failing check', 'checks-progress-failure'),
        segment('danger', optionalFailing, total, 'optional failing check', 'checks-progress-optional-failure'),
        segment('danger', requiredFailing, total, 'required failing check', 'checks-progress-required-failure'),
      ].filter(segmentValue => segmentValue !== undefined)
    : []
  const satisfiedStates = [...(skipped > 0 ? ['skipped'] : []), ...(neutral > 0 ? ['neutral'] : [])]
  const progressLabel = `${successful} of ${total} ${total === 1 ? 'check' : 'checks'} ${formatAlternatives(['successful', ...satisfiedStates])}`
  const workflowApprovalSummary =
    pendingWorkflowCount > 0
      ? `${pendingWorkflowCount} pending workflow ${pendingWorkflowCount === 1 ? 'approval' : 'approvals'}`
      : undefined
  const emptyStatus = sectionStatus ?? statusRollup?.combinedState
  const status = !statusRollup
    ? 'Checks unavailable'
    : total === 0 && emptyStatus && emptyStatus !== 'PASSED'
      ? emptyStatus === 'PENDING_APPROVAL'
        ? (workflowApprovalSummary ?? checksSectionHeading[emptyStatus])
        : checksSectionHeading[emptyStatus]
      : undefined

  return {
    successful,
    total,
    isUnavailable: !statusRollup || total === 0,
    icon:
      statusRollup && total === 0 && emptyStatus
        ? iconByCombinedState[emptyStatus]
        : statusRollup && optionalFailing === failing && optionalFailing > 0 && !hasRequiredBlocker
          ? 'warning'
          : statusRollup
            ? iconByCombinedState[statusRollup.combinedState]
            : 'error',
    status:
      statusRollup &&
      total === 0 &&
      pendingWorkflowCount === 0 &&
      requiredPending > 0 &&
      status !== checksSectionHeading.PENDING_CONFLICTS
        ? 'No check results reported'
        : status,
    progressLabel,
    progressValueText: segments.length ? segments.map(item => item.label).join(', ') : undefined,
    blockingSummary: checksBlockingSummary(
      requiredFailing,
      requiredPending,
      requiredAttention,
      total === 0 &&
        status === `${pendingWorkflowCount} pending workflow ${pendingWorkflowCount === 1 ? 'approval' : 'approvals'}`
        ? 0
        : pendingWorkflowCount,
    ),
    segments,
  }
}

function checksBlockingSummary(
  requiredFailing: number,
  requiredPending: number,
  requiredAttention: number,
  pendingWorkflow: number,
) {
  const summaries: string[] = []
  if (requiredFailing > 0) {
    summaries.push(`${requiredFailing} required ${requiredFailing === 1 ? 'check' : 'checks'} failing`)
  }
  if (requiredPending > 0) {
    summaries.push(`${requiredPending} required ${requiredPending === 1 ? 'check' : 'checks'} pending`)
  }
  if (requiredAttention > 0) {
    summaries.push(`${requiredAttention} required ${requiredAttention === 1 ? 'check needs' : 'checks need'} attention`)
  }
  if (pendingWorkflow > 0) {
    summaries.push(`${pendingWorkflow} workflow ${pendingWorkflow === 1 ? 'approval' : 'approvals'} pending`)
  }

  return summaries.length ? summaries.join(', ') : undefined
}

function formatAlternatives(values: [string, ...string[]]) {
  if (values.length === 1) return values[0]
  if (values.length === 2) return values.join(' or ')
  return `${values.slice(0, -1).join(', ')}, or ${values.at(-1) ?? values[0]}`
}

function countStates(summary: StatusRollup['summary'], states: readonly string[]) {
  const includedStates = new Set(states)
  return summary.reduce((count, item) => count + (includedStates.has(item.state) ? item.count : 0), 0)
}

function countMatchingStates(summary: StatusRollup['summary'], predicate: (state: StatusCheckState) => boolean) {
  return summary.reduce((count, item) => count + (predicate(item.state) ? item.count : 0), 0)
}

function isAttentionRequiredStatus(state: StatusCheckState) {
  return attentionRequiredStates.some(attentionState => attentionState === state)
}

function segment(
  color: ProgressSegment['color'],
  count: number,
  total: number,
  label: string,
  testId: string,
  pluralLabel = `${label}s`,
): ProgressSegment | undefined {
  if (count <= 0) return undefined
  return {
    color,
    id: testId,
    label: `${count} ${count === 1 ? label : pluralLabel}`,
    percentage: (count / total) * 100,
    testId,
  }
}
